<app-side-menu>
  <div class="approval-container">
    <!-- Loading -->
    <div *ngIf="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
    </div>

    <!-- Error Message -->
    <div *ngIf="error" class="alert alert-danger" role="alert">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <i class="fas fa-exclamation-triangle me-2"></i>
          {{ error }}
        </div>
        <button 
          type="button" 
          class="btn btn-outline-danger btn-sm"
          (click)="retryLoad()">
          <i class="fas fa-redo me-1"></i>
          Tentar Novamente
        </button>
      </div>
    </div>

    <!-- Conteúdo Principal -->
    <div *ngIf="!isLoading && !error && purchaseOrder">
      <!-- Cabeçalho -->
      <div class="approval-header">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h1 class="approval-title">
              <i class="fas fa-check-circle me-2"></i>
              {{ approvalPageTitle }}
            </h1>
            <p class="approval-subtitle">
              Solicitação {{ purchaseOrder.orderNumber }} — {{ formatDate(purchaseOrder.requestDate) }}
            </p>
          </div>
          <div>
            <button 
              type="button" 
              class="btn btn-outline-secondary"
              (click)="goBack()">
              <i class="fas fa-arrow-left me-1"></i>
              Voltar
            </button>
          </div>
        </div>
      </div>

      <!-- Retorno da API (erros na decisão do gestor), além do toast -->
      <div
        *ngIf="purchaseOrder && lastManagerDecisionApiText"
        class="alert alert-danger mb-4 manager-decision-api-feedback"
        role="alert">
        <strong class="d-block mb-2">
          <i class="fas fa-server me-2"></i>Resposta do servidor
        </strong>
        <pre class="mb-0">{{ lastManagerDecisionApiText }}</pre>
      </div>

      <!-- Resumo da solicitação -->
      <div class="row mb-4">
        <div class="col-md-8">
          <div class="info-card">
            <h5 class="card-title">
              <i class="fas fa-info-circle me-2"></i>
              Resumo da solicitação
            </h5>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="info-row">
                    <label>Número:</label>
                    <span>{{ purchaseOrder.orderNumber }}</span>
                  </div>
                  <div class="info-row">
                    <label>Status:</label>
                    <span class="badge" [class]="getStatusClass(purchaseOrder.status)">
                      {{ getStatusDisplayName(purchaseOrder.status) }}
                    </span>
                  </div>
                  <div class="info-row">
                    <label>Prioridade:</label>
                    <span class="badge" [class]="getPriorityClass(purchaseOrder.priority)">
                      {{ getPriorityDisplayName(purchaseOrder.priority) }}
                    </span>
                  </div>
                  <div class="info-row">
                    <label>Solicitante:</label>
                    <span>{{ purchaseOrder.requestedByStaffName || 'Não informado' }}</span>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="info-row" *ngIf="purchaseOrder.costCenterName">
                    <label>Centro de custo:</label>
                    <span>{{ purchaseOrder.costCenterName }}</span>
                  </div>
                  <div class="info-row" *ngIf="purchaseOrder.description">
                    <label>Descrição:</label>
                    <span>{{ purchaseOrder.description }}</span>
                  </div>
                  <div class="info-row" *ngIf="purchaseOrder.notes">
                    <label>Observações:</label>
                    <span>{{ purchaseOrder.notes }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="info-card budget-card">
            <h5 class="card-title">
              <i class="fas fa-wallet me-2"></i>
              Verba do centro de custo
            </h5>
            <div class="card-body">
              <div *ngIf="isLoadingBalance" class="budget-loading">
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Consultando saldo...
              </div>

              <div
                *ngIf="!isLoadingBalance && balanceError"
                class="alert alert-warning mb-0">
                <i class="fas fa-exclamation-triangle me-2"></i>
                {{ balanceError }}
              </div>

              <ng-container
                *ngIf="!isLoadingBalance && !balanceError && costCenterBalance">
                <div class="budget-summary">
                  <div class="budget-name">
                    {{ costCenterBalance.costCenterName }}
                    <small *ngIf="costCenterBalance.costCenterCode" class="budget-code">
                      ({{ costCenterBalance.costCenterCode }})
                    </small>
                  </div>
                  <div
                    class="budget-amount"
                    [class.budget-amount--ok]="costCenterBalance.availableBalance > 0"
                    [class.budget-amount--warn]="costCenterBalance.availableBalance <= 0">
                    {{ formatCurrency(costCenterBalance.availableBalance) }}
                  </div>
                  <div
                    class="alert mb-0"
                    [class.alert-success]="costCenterBalance.availableBalance > 0"
                    [class.alert-danger]="costCenterBalance.availableBalance <= 0">
                    <ng-container *ngIf="costCenterBalance.availableBalance > 0">
                      <i class="fas fa-check-circle me-2"></i>
                      Há verba disponível para esta solicitação.
                    </ng-container>
                    <ng-container *ngIf="costCenterBalance.availableBalance <= 0">
                      <i class="fas fa-times-circle me-2"></i>
                      O centro de custo não possui verba disponível.
                    </ng-container>
                  </div>
                </div>
              </ng-container>

              <div
                *ngIf="!isLoadingBalance && !balanceError && !costCenterBalance"
                class="alert alert-secondary mb-0">
                <i class="fas fa-info-circle me-2"></i>
                Centro de custo não informado nesta solicitação.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Itens da solicitação (sem preços) -->
      <div class="row mb-4" *ngIf="requestItems.length > 0">
        <div class="col-12">
          <div class="info-card">
            <h5 class="card-title">
              <i class="fas fa-list-ul me-2"></i>
              Itens solicitados
            </h5>
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-sm align-middle mb-0 request-items-table">
                  <thead>
                    <tr>
                      <th style="width:55%">Produto</th>
                      <th class="text-end" style="width:10%">Qtd</th>
                      <th style="width:10%">Unidade</th>
                      <th style="width:25%">Justificativa</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let it of requestItems">
                      <td>{{ it.productName || '—' }}</td>
                      <td class="text-end">{{ it.quantityRequested ?? 0 }}</td>
                      <td>{{ it.unit || '—' }}</td>
                      <td class="request-items-justification">
                        {{ it.justification || '—' }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Formulário de Aprovação -->
      <div class="approval-form-card" *ngIf="canApprove()">
        <h5 class="card-title">
          <i class="fas fa-edit me-2"></i>
          Decisão de Aprovação
        </h5>
        <div class="card-body">
          <!-- Seleção da Ação -->
          <div class="mb-4">
            <label class="form-label fw-bold">Selecione sua decisão:</label>
            <div class="action-buttons">
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="radio" 
                  name="approvalAction" 
                  id="approve"
                  value="approve"
                  [(ngModel)]="approvalForm.action">
                <label class="form-check-label btn-approve-option" for="approve">
                  <i class="fas fa-check me-2"></i>
                  Aprovar
                </label>
              </div>
              
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="radio" 
                  name="approvalAction" 
                  id="reject"
                  value="reject"
                  [(ngModel)]="approvalForm.action">
                <label class="form-check-label btn-reject-option" for="reject">
                  <i class="fas fa-times me-2"></i>
                  Reprovar
                </label>
              </div>
              
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="radio" 
                  name="approvalAction" 
                  id="requestChanges"
                  value="request_changes"
                  [(ngModel)]="approvalForm.action">
                <label class="form-check-label btn-changes-option" for="requestChanges">
                  <i class="fas fa-edit me-2"></i>
                  Solicitar Alterações
                </label>
              </div>
            </div>
          </div>

          <!-- Campos Condicionais -->
          <div class="conditional-fields">
            <!-- Comentários Gerais (sempre visível) -->
            <div class="mb-3">
              <label for="comments" class="form-label">Comentários (opcional):</label>
              <textarea 
                id="comments"
                class="form-control"
                rows="3"
                placeholder="Adicione observações sobre sua decisão..."
                [(ngModel)]="approvalForm.comments">
              </textarea>
            </div>

            <!-- Campo para Rejeição -->
            <div class="mb-3" *ngIf="approvalForm.action === 'reject'">
              <label for="rejectionReason" class="form-label">
                Motivo da Rejeição <span class="text-danger">*</span>:
              </label>
              <textarea 
                id="rejectionReason"
                class="form-control"
                rows="3"
                placeholder="Explique detalhadamente o motivo da rejeição..."
                [(ngModel)]="approvalForm.rejectionReason"
                required>
              </textarea>
            </div>

            <!-- Campo para Solicitação de Alterações -->
            <div class="mb-3" *ngIf="approvalForm.action === 'request_changes'">
              <label for="requestedChanges" class="form-label">
                Alterações Solicitadas <span class="text-danger">*</span>:
              </label>
              <textarea 
                id="requestedChanges"
                class="form-control"
                rows="3"
                placeholder="Descreva as alterações necessárias..."
                [(ngModel)]="approvalForm.requestedChanges"
                required>
              </textarea>
            </div>
          </div>

          <!-- Botões de Ação -->
          <div class="approval-actions">
            <button 
              type="button" 
              class="btn btn-secondary me-2"
              (click)="goBack()">
              <i class="fas fa-times me-1"></i>
              Cancelar
            </button>
            
            <button 
              type="button"
              class="btn btn-success"
              *ngIf="approvalForm.action === 'approve'"
              [disabled]="!isFormValid()"
              (click)="approve()">
              <i class="fas fa-check me-1"></i>
              Confirmar aprovação
            </button>
            
            <button 
              type="button" 
              class="btn btn-danger"
              *ngIf="approvalForm.action === 'reject'"
              [disabled]="!isFormValid()"
              (click)="reject()">
              <i class="fas fa-times me-1"></i>
              Confirmar reprovação
            </button>
            
            <button 
              type="button" 
              class="btn btn-warning"
              *ngIf="approvalForm.action === 'request_changes'"
              [disabled]="!isFormValid()"
              (click)="requestChanges()">
              <i class="fas fa-edit me-1"></i>
              Solicitar Alterações
            </button>
          </div>
        </div>
      </div>

      <!-- Aviso quando o usuário logado NÃO é o gestor designado da solicitação -->
      <div class="info-card" *ngIf="!isDesignatedManager()">
        <h5 class="card-title">
          <i class="fas fa-user-shield me-2"></i>
          Aprovação restrita ao gestor designado
        </h5>
        <div class="card-body">
          <div class="alert alert-warning mb-0">
            <p class="mb-0">
              <i class="fas fa-info-circle me-2"></i>
              {{ notDesignatedManagerMessage }}
            </p>
          </div>
        </div>
      </div>

      <!-- Aviso quando o status não permite aprovação (e o usuário JÁ é o gestor) -->
      <div class="info-card" *ngIf="isDesignatedManager() && !canApprove()">
        <h5 class="card-title">
          <i class="fas fa-info-circle me-2"></i>
          Aprovação não disponível
        </h5>
        <div class="card-body">
          <div class="alert alert-info mb-0">
            <p class="mb-0">
              Esta solicitação não está disponível para aprovação no momento.
              Status atual: <strong>{{ getStatusDisplayName(purchaseOrder.status) }}</strong>
            </p>
          </div>
        </div>
      </div>

      <!-- Histórico de Aprovações -->
      <div class="info-card mt-4" *ngIf="approvals.length > 0">
        <h5 class="card-title">
          <i class="fas fa-history me-2"></i>
          Histórico de Aprovações
        </h5>
        <div class="card-body">
          <div class="approval-timeline">
            <div 
              *ngFor="let approval of approvals; let last = last"
              class="approval-item"
              [class.approval-item-last]="last">
              <div class="approval-icon">
                <i class="fas fa-user"></i>
              </div>
              <div class="approval-content">
                <div class="approval-header">
                  <strong>{{ approval.approverStaffName }}</strong>
                  <span class="badge ms-2 bg-info">
                    Nível {{ approval.approvalLevel }}
                  </span>
                </div>
                <div class="approval-date">
                  {{ formatDate(approval.createdAt) }}
                </div>
                <div class="approval-status">
                  Status: <strong>{{ approval.status }}</strong>
                </div>
                <div class="approval-comments" *ngIf="approval.comments">
                  {{ approval.comments }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</app-side-menu>
